import {ReactNode} from 'react';
import {connect} from 'react-redux';

import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {getUser, getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {getFlatTeams, getUserTeams} from '../../../../store/data/teams/teamsSelectors';
import {NamespaceDropdownBox} from './namespaceDropdownBox';

/*
 * Props.
 */

interface NamespaceDropdownBoxContainerProps {
  resourceNamespace: string | undefined;
  permission: PermissionsEnum;
  messageForDisplayOnly?: ReactNode;
  isDisplayOnly?: boolean;
  shouldHideSandbox?: boolean;
  shouldRenderCompanyDropdownItem?: boolean;
  shouldRenderSharedDropdownItem?: boolean;
  shouldRenderVisibilityDescription?: boolean;
  shouldHideUserNamespaceChoice?: boolean;
  shouldGlobalAdminAccessAllTeams?: boolean;
}

/*
 * Selectors.
 */

function getResourceNamespace(_state: AppState, props: NamespaceDropdownBoxContainerProps) {
  return props.resourceNamespace;
}

function getIsDisplayOnly(_state: AppState, props: NamespaceDropdownBoxContainerProps) {
  return props.isDisplayOnly;
}

function getPermission(_state: AppState, props: NamespaceDropdownBoxContainerProps) {
  return props.permission;
}

function getShouldHideUserNamespaceOption(_state: AppState, props: NamespaceDropdownBoxContainerProps) {
  return props.shouldHideUserNamespaceChoice;
}

function getShouldGlobalAdminAccessAllTeams(_state: AppState, props: NamespaceDropdownBoxContainerProps) {
  return props.shouldGlobalAdminAccessAllTeams;
}

function getShouldHideSandbox(_state: AppState, props: NamespaceDropdownBoxContainerProps) {
  return props.shouldHideSandbox;
}

function makeGetUserNamespaceChoice() {
  return createSelector(
    [getUserNamespace, getShouldHideUserNamespaceOption],
    (userNamespace, shouldHideUserNamespaceOption) =>
      shouldHideUserNamespaceOption ? undefined : userNamespace,
  );
}

function makeGetTeams() {
  return createSelector(
    [
      getUser,
      getUserNamespace,
      getIsDisplayOnly,
      getUserTeams,
      getFlatTeams,
      getIsUserGlobalAdmin,
      getShouldGlobalAdminAccessAllTeams,
      getResourceNamespace,
      getHasPermission,
      getPermission,
      getShouldHideSandbox,
    ],
    (
      user,
      userNamespace,
      isDisplayOnly,
      teams,
      allTeams,
      isUserGlobalAdmin,
      shouldGlobalAdminAccessAllTeams,
      resourceNamespace,
      hasPermission,
      permission,
      shouldNotRenderSandbox,
    ) => {
      const excludeSandbox = (t: TeamModel) => !shouldNotRenderSandbox || !t.isSandbox;

      // If the user is a global admin, return all teams.
      // A user may just be a "member" of a team, but in fact they do have all permissions to act
      // as an admin due to their global admin status.
      if (isUserGlobalAdmin && shouldGlobalAdminAccessAllTeams) {
        return allTeams.filter(excludeSandbox);
      }

      // If the provided resource namespace is the user namespace or only for display, allow all of our teams.
      if (resourceNamespace === userNamespace || isDisplayOnly) {
        return teams.filter((t) => hasPermission(t.namespace, permission)).filter(excludeSandbox);
      }

      // If this is the namespace of a team that we're part of, use it.
      const team = teams.find(
        (t) => t.namespace === resourceNamespace && hasPermission(t.namespace, permission),
      );
      if (team) {
        return [team];
      }

      // Otherwise, don't allow any team.
      return [];
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<NamespaceDropdownBoxContainerProps>()(() => ({
  userNamespace: makeGetUserNamespaceChoice(),
  teams: makeGetTeams(),
}));

export const NamespaceDropdownBoxContainer = connect(mapStateToProps)(NamespaceDropdownBox);
